import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin, Result } from 'antd';
import {ClientService} from "../services/clients/ClientService";
import {extractSubdomainSlug} from "../utils/extractSubdomainSlug";
import {PublicBranding} from "../model/clients/PublicClientConfig";
import {PermissionsService} from "../services/permissions/PermissionsService";
import {forceTokenRefresh} from "./auth/tokenRefreshCoordinator";
import {useIsFortiusAdmin} from "../hooks/useIsFortiusAdmin";

// Auth0 namespace for the custom claims stamped by the `add-client-access`
// action. Matches `${AUTH0_API_IDENTIFIER}:` in auth0/actions/add-client-access
// and the existing `user['fortius-app:clients']` / `user['fortius-app:roles']`
// readers in this codebase.
const PENDING_CLIENT_SLUG_CLAIM = 'fortius-app:pending_client_slug';


interface ClientContextType {
    currentClientId: string;
    setCurrentClientId: (clientId: string) => void;
    availableClients: string[];
}

interface ClientRedirectConfig {
    clientId: string;
    url: string;
}

// Example redirect configuration - can be moved to environment variables or API later
const CLIENT_REDIRECTS: ClientRedirectConfig[] = [
    // { clientId: 'Other Club', url: 'https://otherclub.com' }
];

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Pre-login slug → clientId resolution state. While `pending`, we render a
// spinner; once `resolved` we use the URL-derived tenant and skip the Auth0
// claims path; `apex` falls back to the legacy multi-tenant flow.
type SlugResolution =
    | { state: 'pending' }
    | { state: 'resolved'; clientId: string; branding: PublicBranding }
    | { state: 'apex' };

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();
    const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
    const [slugResolution, setSlugResolution] = useState<SlugResolution>(() => {
        const slug = extractSubdomainSlug(window.location.hostname);
        return slug ? { state: 'pending' } : { state: 'apex' };
    });
    const [isInitialized, setIsInitialized] = useState(false);
    const [currentClientId, setCurrentClientId] = useState<string | null>(
        localStorage.getItem('currentClientName')
    );
    const [availableClients, setAvailableClients] = useState<string[]>([]);
    const hasAttemptedSyncRef = useRef(false);
    // Cross-tenant role read straight off the ID token — no /auth/sync round trip,
    // so it is already true on the very first bootstrap pass, before any tenant is
    // selected. That matters here: the per-tenant permission set (which is what
    // App.tsx checks for AdminPermissions.FortiusAdmin) needs an X-Client-Id to
    // fetch, and picking the client is exactly what this provider is trying to do.
    const isFortiusAdmin = useIsFortiusAdmin();
    // One widening attempt per mount. The bootstrap effect below is guarded on
    // `availableClients.length === 0`, so it cannot re-fire once the claim has
    // populated the list; this ref covers the remaining case where the Auth0 SDK
    // hands us a new `user` object (e.g. after a token refresh) while the widened
    // list is already in place.
    const hasWidenedForAdminRef = useRef(false);

    // Create a standalone instance of ClientService just for fetching initial clients
    const initialClientService = useMemo(() =>
            new ClientService(getAccessTokenSilently, null),
        [getAccessTokenSilently]
    );

    // FORT-147: resolve the tenant from the hostname before Auth0 finishes.
    // The slug → clientName lookup is unauthenticated and idempotent, so it's
    // safe to fire on every mount. On 404 we fall back to the apex flow so a
    // misconfigured DNS doesn't lock the user out entirely.
    useEffect(() => {
        if (slugResolution.state !== 'pending') return;

        const slug = extractSubdomainSlug(window.location.hostname);
        if (!slug) {
            setSlugResolution({ state: 'apex' });
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const config = await ClientService.getPublicConfigBySubdomain(slug);
                if (cancelled) return;
                if (!config) {
                    setSlugResolution({ state: 'apex' });
                    return;
                }
                setSlugResolution({
                    state: 'resolved',
                    clientId: config.clientName,
                    branding: config.branding ?? {},
                });
                localStorage.setItem('currentClientName', config.clientName);
                setCurrentClientId(config.clientName);
                setAvailableClients([config.clientName]);
            } catch (error) {
                if (cancelled) return;
                console.error('Subdomain client resolution failed:', error);
                setSlugResolution({ state: 'apex' });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [slugResolution.state]);

    // Fortius admins are not confined to the clubs stamped on their token: the API
    // admits `admin:fortius` into any tenant (ClientContextAuthorizationHandler
    // short-circuits on the permission before any tenancy test) and `GET /clients`
    // returns every tenant. The claim, though, only grows when an admin is
    // explicitly assigned a club, so a newly-created tenant never appeared in the
    // switcher for an admin who already had one — the claim-empty fallback below is
    // the only thing that ever consulted /clients. Union the two instead of choosing
    // between them, so every future tenant shows up without an Auth0 change.
    //
    // Strictly append-only, and run after the selection has already been made: the
    // claim's clubs keep their order at the front of the list, so a non-admin's
    // options are untouched and an admin's existing selection — and the
    // `X-Client-Id` header derived from it — does not move.
    const widenAvailableClientsForFortiusAdmin = useCallback(async (claimClients: string[]) => {
        // Set when the bootstrap deferred to us because it could not vouch for the
        // saved club (see `validSavedClient`): render is held until we either
        // confirm that club against the real tenant list or fall back, so no child
        // mounts and fetches under a tenant the API would refuse.
        const savedClient = localStorage.getItem('currentClientName');
        const savedClientNeedsVouching = !!savedClient
            && claimClients.length > 0
            && !claimClients.includes(savedClient);

        const fallBackToClaim = () => {
            const fallback = claimClients[0];
            setCurrentClientId(fallback);
            localStorage.setItem('currentClientName', fallback);
        };

        try {
            const allClients = await initialClientService.getAvailableClients();
            // ClubBookingApiClient resolves `undefined` on a failed request rather
            // than throwing, so an unauthorised or errored /clients lands here.
            if (!allClients || allClients.length === 0) {
                // Couldn't verify means don't trust: an unvouched club would leave the
                // admin stamping an X-Client-Id the API refuses, with no way back when
                // their claim names a single club (ClientSelector hides itself at one).
                if (savedClientNeedsVouching) fallBackToClaim();
                return;
            }

            setAvailableClients(previous => {
                const extras = allClients.filter(client => !previous.includes(client));
                // Same array identity when there is nothing to add, so an admin whose
                // claim already covers every tenant doesn't re-render for nothing.
                return extras.length === 0 ? previous : [...previous, ...extras];
            });

            // Now the real tenant list has arrived, drop a saved club that exists in
            // neither it nor the claim rather than pinning the admin to a dead tenant.
            if (savedClientNeedsVouching && !allClients.includes(savedClient!)) {
                fallBackToClaim();
            }
        } catch (error) {
            // Non-fatal: the admin keeps the clubs from their claim.
            console.error('Error widening client list for Fortius admin:', error);
            if (savedClientNeedsVouching) fallBackToClaim();
        } finally {
            // Whatever happened, the tenant is now settled — release the render the
            // bootstrap held back for us.
            if (savedClientNeedsVouching) setIsInitialized(true);
        }
    }, [initialClientService]);

    // Reset the one-shot flags when user logs out, so a logout -> login within the
    // same document (no full reload) bootstraps from scratch.
    useEffect(() => {
        if (!isAuthenticated) {
            hasAttemptedSyncRef.current = false;
            hasWidenedForAdminRef.current = false;
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Slug-resolved tenants skip the Auth0 claims path entirely — the URL
        // is the source of truth. We still wait for auth to complete so the
        // downstream services have a token, but we don't override the
        // URL-derived clientId.
        if (slugResolution.state === 'resolved') {
            if (!authLoading && isAuthenticated) setIsInitialized(true);
            return;
        }
        if (slugResolution.state !== 'apex') return;

        if (!authLoading && isAuthenticated && user && availableClients.length === 0) {
            const clients = user['fortius-app:clients'] || [];
            setAvailableClients(clients);

            if (clients.length > 0) {
                const savedClient = localStorage.getItem('currentClientName');
                // A Fortius admin can legitimately be sitting on a club the claim
                // doesn't carry — the API admits them and the widened switcher
                // offers it. Validating against the claim alone reset the selection
                // on every reload, so switching to a demo club never stuck. Trust
                // the saved value for an admin here and let the widening reconcile
                // it against the real tenant list; a non-admin is still held to the
                // claim exactly as before.
                const validSavedClient = savedClient
                    && (clients.includes(savedClient) || isFortiusAdmin);

                if (validSavedClient) {
                    setCurrentClientId(savedClient);
                } else {
                    const defaultClient = clients[0];
                    setCurrentClientId(defaultClient);
                    localStorage.setItem('currentClientName', defaultClient);
                }

                const widening = isFortiusAdmin && !hasWidenedForAdminRef.current;
                if (widening) {
                    hasWidenedForAdminRef.current = true;
                    widenAvailableClientsForFortiusAdmin(clients);
                }

                // A saved club the claim doesn't carry was accepted on trust above,
                // so hold render until the widening has checked it against the real
                // tenant list — otherwise children mount and fetch under a tenant the
                // API may refuse, and the correction arrives after the damage. Every
                // other case is already settled, so render now and let the widening
                // grow the switcher's options behind it.
                const awaitingVouching = widening
                    && !!savedClient
                    && !clients.includes(savedClient);
                if (!awaitingVouching) setIsInitialized(true);
            } else {
                // An empty claim already falls through to GET /clients, so a
                // Fortius admin on this path sees every tenant without help.
                getAndSetFortiusClients();
            }
        }
        // Tenant bootstrap is intentionally keyed on auth/slug resolution only;
        // availableClients (guarded above) and getAndSetFortiusClients are excluded
        // to avoid re-initialising the client on every render. isFortiusAdmin and
        // widenAvailableClientsForFortiusAdmin are safe to omit for the same reason:
        // the former is derived from `user`, which IS a dependency, and the latter is
        // memoised on initialClientService, which is memoised on getAccessTokenSilently.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isAuthenticated, authLoading, slugResolution.state]);

    const setClientsFromList = (clients: string[]) => {
        setAvailableClients(clients);
        // Honour an existing selection when the fetched list still contains it.
        // Unconditionally taking clients[0] meant a reload after a club switch
        // snapped back to the first club, so switching never stuck on this path
        // either. A brand-new signup has just written its resolved club here, so
        // it is picked up rather than overridden.
        const savedClient = localStorage.getItem('currentClientName');
        const selected = savedClient && clients.includes(savedClient) ? savedClient : clients[0];
        setCurrentClientId(selected);
        localStorage.setItem('currentClientName', selected);
    };

    const getAndSetFortiusClients = async () => {
        try {
            // New-signup short-circuit: if the token carries pending_client_slug,
            // the user just registered against a slug and hasn't been provisioned
            // yet. They aren't a Fortius admin, so /clients (admin-only) would
            // 403 anyway — go straight to the sync flow.
            const pendingSlug = (user && (user[PENDING_CLIENT_SLUG_CLAIM] as string | undefined)) || null;
            if (pendingSlug && !hasAttemptedSyncRef.current) {
                hasAttemptedSyncRef.current = true;
                await syncAndRefreshClients();
                return;
            }

            const clients = await initialClientService.getAvailableClients();
            if (clients && clients.length > 0) {
                setClientsFromList(clients);
                setIsInitialized(true);
                return;
            }

            // No clients found — likely a Fortius admin in a transitional state
            // where the token hasn't been refreshed since the Auth0 Action ran.
            // Refresh and re-fetch.
            if (!hasAttemptedSyncRef.current) {
                hasAttemptedSyncRef.current = true;
                await syncAndRefreshClients();
                return;
            }

            setIsInitialized(true);
        } catch (error) {
            console.error('Error fetching clients:', error);
            setIsInitialized(true); // Set initialized even on error to prevent infinite loading
        }
    };

    const syncAndRefreshClients = async () => {
        try {
            // Pending-slug path: a new signup carried `?client_slug=` which
            // the Auth0 PostUserRegistration action stamped into user_metadata
            // and the PostLogin action surfaced as a custom claim. The
            // backend, not Auth0, owns slug → client name resolution and
            // membership creation. Pattern:
            //   1. Resolve slug -> canonical client name via the
            //      unauthenticated public endpoint.
            //   2. Call the existing authenticated /auth/sync with that name
            //      as X-Client-Id. AuthSyncService runs
            //      Auth0Service.AddClientAndSetMembershipId, which writes
            //      app_metadata.clients and clears pending_client_slug.
            //   3. Force-refresh the token so the populated `clients` claim
            //      lands on the next API call.
            const pendingSlug = (user && (user[PENDING_CLIENT_SLUG_CLAIM] as string | undefined)) || null;
            if (pendingSlug) {
                const config = await ClientService.getPublicConfigBySubdomain(pendingSlug);
                if (config?.clientName) {
                    // Stage the resolved name so the next API call carries it
                    // as X-Client-Id. ClubBookingApiClient reads `currentClientName`
                    // from localStorage to populate the header.
                    localStorage.setItem('currentClientName', config.clientName);
                    setCurrentClientId(config.clientName);

                    const permissionsService = new PermissionsService(getAccessTokenSilently, config.clientName);
                    await permissionsService.syncAuth();
                } else {
                    console.warn(
                        '[ClientProvider] pending_client_slug claim present but slug did not resolve to a client:',
                        pendingSlug,
                    );
                }
            }

            // Force a token refresh so the Auth0 Action's updated claims
            // (e.g. fortius-app:clients) are included in the new token. After
            // /auth/sync above, app_metadata.clients is populated server-side;
            // this refresh is what surfaces it to the SPA. Routed through the
            // shared coordinator (FORT-293) so a concurrent MemberContext /
            // ClientConfigProvider refresh on the same first paint collapses to
            // one network call.
            await forceTokenRefresh(getAccessTokenSilently);

            // Re-fetch clients after token refresh — the useEffect watching
            // `user` will also re-fire when the Auth0 SDK updates the user
            // object, but we try the API as a direct fallback.
            const clients = await initialClientService.getAvailableClients();
            if (clients && clients.length > 0) {
                setClientsFromList(clients);
            }
        } catch (error) {
            console.error('Error during post-signup sync:', error);
        } finally {
            setIsInitialized(true);
        }
    };

    const handleClientSwitch = (clientId: string) => {
        // Slug-resolved tenants are locked to the URL — switching would land
        // on a hostname that doesn't match the new client. The picker is
        // hidden in that case, but defend in depth.
        if (slugResolution.state === 'resolved') {
            console.warn('Client switching is disabled on slug-routed hostnames');
            return;
        }

        // Check for redirect configuration
        const redirectConfig = CLIENT_REDIRECTS.find(config => config.clientId === clientId);
        if (redirectConfig) {
            // Redirect to different website
            window.location.href = redirectConfig.url;
            return;
        }

        // Update local storage and state
        localStorage.setItem('currentClientName', clientId);
        setCurrentClientId(clientId);

        // Refresh the current page
        window.location.reload();
    };

    if (slugResolution.state === 'pending' || authLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <Spin size="large" tip="Loading client information..." />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <>{children}</>;
    }

    // Still resolving the tenant (slug lookup / client bootstrap in flight) — a
    // genuine loading state.
    if (!isInitialized) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <Spin size="large" tip="Loading client information..." />
            </div>
        );
    }

    // Resolution is EXHAUSTED (isInitialized) but no tenant could be determined:
    // an authenticated user on a non-tenant host (e.g. the bare apex) with an
    // empty `clients` claim and no resolvable slug. Previously this fell through
    // to the spinner above and span forever (FORT-291). Show an actionable
    // next-step splash instead so onboarding never hangs. status="info" (not an
    // error) per the apps/admin-web/CLAUDE.md no-tenant-render convention; the
    // copy also covers a transient resolution blip via "refresh".
    if (!currentClientId) {
        return (
            <div
                data-testid="no-club-resolved"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}
            >
                <Result
                    status="info"
                    title="We couldn't determine your club"
                    subTitle="Open your club's own sign-in link (for example https://yourclub.fortius-app.com) to continue, or refresh the page. If this keeps happening, contact your club for help."
                />
            </div>
        );
    }

    return (
        <ClientContext.Provider value={{
            currentClientId,
            setCurrentClientId: handleClientSwitch,
            availableClients
        }}>
            {children}
        </ClientContext.Provider>
    );
};

export const useClient = (): ClientContextType => {
    const context = useContext(ClientContext);
    if (!context) {
        throw new Error('useClient must be used within a ClientProvider');
    }
    return context;
};

/**
 * Non-throwing variant of {@link useClient}. Returns `undefined` when the caller is rendered
 * outside the `ClientContext.Provider` — which happens during the unauthenticated render pass,
 * where {@link ClientProvider} returns children as a bare fragment without wrapping them.
 *
 * Use this in components whose render path can execute before authentication is established
 * (e.g. `MemberProvider`, which sits inside `ClientProvider` but still renders while the user
 * is logging in).
 */
export const useClientOptional = (): ClientContextType | undefined => {
    return useContext(ClientContext);
};
